import { useParams, useSearchParams } from "react-router-dom"
import { useCurrentStep } from "./useCurrentStep"
import { useCallback, useEffect, useMemo } from "react"
import { useCurrentEntity } from "./useCurrentEntity"
import { COLORS, PinColorType } from "../configs/pinColors"
import { useGetMoneyFlow } from "../api/case/document"
import { useGetSankeyData } from "../api/sankeyData/get.ts"
import { addBlockchainPrefix } from "../backend/helpers/formatBlockchainAddress.ts"
import { useFindEdgeByTxHash } from "../api/moneyflow/moneyflow.ts"
import { isValidAddress } from "../backend/helpers/validateBlockchainAddress.ts"

export type EntityResult = {
    _id: string,
    entityID: string,
    color: PinColorType
}

export type EdgeResult = {
    _id: string,
    edgeID: string,
    color: PinColorType
}

export type FoundAddressType = {
    [key: string]: EntityResult | EdgeResult,
}

export function hasEntityID(obj: FoundAddressType[string]): obj is EntityResult {
    return "entityID" in obj
}

export function hasEdgeID(obj: FoundAddressType[string]): obj is EdgeResult {
    return "edgeID" in obj
}

export const useSearchAddress = () => {
    const { documentId } = useParams()
    const { currentStep } = useCurrentStep()
    const { setCurrentEntity } = useCurrentEntity()
    const [searchParams, setSearchParams] = useSearchParams()

    const urlSearchAddresses = useMemo(() => searchParams.getAll('find'), [searchParams])
    const { data: moneyflow } = useGetMoneyFlow(documentId!)
    const { data: sankeyData } = useGetSankeyData(moneyflow?.sankeyData[currentStep])

    const addressesToFind = useMemo(
        () => urlSearchAddresses.filter((address) => isValidAddress(address, moneyflow!.blockchain)),
        [moneyflow, urlSearchAddresses]
    )

    const transactionsToFind = useMemo(
        () => urlSearchAddresses.filter((address) => !isValidAddress(address, moneyflow!.blockchain)),
        [moneyflow, urlSearchAddresses]
    )
    const {
        data: txSearchResults, isPending: loading
    } = useFindEdgeByTxHash(documentId!, transactionsToFind, currentStep)

    const deleteAddress = useCallback((value: string) => {
        const newSearchParams = new URLSearchParams(searchParams)
        newSearchParams.delete('find', value)
        setSearchParams(newSearchParams)
    }, [searchParams, setSearchParams])

    const addAddress = useCallback((value: string) => {
        const newSearchParams = new URLSearchParams(searchParams)
        newSearchParams.append('find', value)
        setSearchParams(newSearchParams)
    }, [searchParams, setSearchParams])

    const clearSearch = useCallback(() => {
        const newSearchParams = new URLSearchParams(searchParams)
        newSearchParams.delete('find')
        setSearchParams(newSearchParams)
    }, [searchParams, setSearchParams])

    const searchResult: {
        entitiesFound: { [key: string]: EntityResult },
        edgesFound: { [key: string]: EdgeResult },
        notFound: string[]
    } = useMemo(() => {
        const result: {
            entitiesFound: { [key: string]: EntityResult },
            edgesFound: { [key: string]: EdgeResult },
            notFound: string[]
        } = {
            entitiesFound: {},
            edgesFound: {},
            notFound: []
        }
        let colorCounter = 0

        const entitiesCount = sankeyData?.Entities.length ?? 0

        addressesToFind.forEach((address) => {
            let found = false
            for (let i = 0; i < entitiesCount; i++) {
                const entity = sankeyData!.Entities[i]

                if (entity.AddressesLabeled.find((addressLabeled) => {
                    const addressPrefixed = addBlockchainPrefix(addressLabeled.Address, moneyflow!.blockchain)
                    return addressPrefixed === address
                }) && !result.entitiesFound[address]) {
                    colorCounter++
                    found = true
                    result.entitiesFound[address] = {
                        _id: entity._id ?? entity.ID,
                        entityID: entity.ID,
                        color: COLORS[colorCounter]
                    }
                }
            }
            if (!found) {
                deleteAddress(address)
                result.notFound.push(address)
            }
        })

        txSearchResults?.foundEdges?.forEach((obj) => {
            colorCounter++
            result.edgesFound[obj.address] = {
                _id: obj._id,
                edgeID: obj.edgeID,
                color: COLORS[colorCounter]
            }
        })

        txSearchResults?.notFoundTx?.forEach((address) => {
            deleteAddress(address)
            result.notFound.push(address)
        })

        return result
    }, [addressesToFind, deleteAddress, moneyflow, sankeyData, txSearchResults])

    useEffect(() => {
        const lastResultKey = addressesToFind.at(-1) ?? ''
        const lastResultEntity = searchResult.entitiesFound[lastResultKey]
        const lastResultEdge = searchResult.edgesFound[lastResultKey]
        if (lastResultEntity) {
            setCurrentEntity(lastResultEntity.entityID)
        } else if (lastResultEdge) (
            setCurrentEntity(lastResultEdge.edgeID)
        )
    }, [addressesToFind, searchResult, setCurrentEntity])

    return {
        loading: transactionsToFind.length > 0 && loading,
        searchResult,
        deleteAddress,
        clearSearch,
        addAddress
    }
}
